import { type DragEndEvent } from "@dnd-kit/core"
import { arrayMove } from "@dnd-kit/sortable"
import { IconSparkles } from "@tabler/icons-react"
import { useLayoutEffect, useState } from "react"
import { Link } from "react-router"
import styled from "styled-components"

import Tooltip from "@forento/ui/components/Tooltip"
import { cn } from "@forento/ui/utilities/tailwind"

import Button from "~/components/Button"
import { AddIcon, ArrowIcon, ExamIcon, SettingsIcon } from "~/components/Icon"
import { useAlert } from "~/contexts/AlertContext"
import SortableListContext from "~/contexts/SortableListContext"
import { usePlatform } from "~/contexts/UserContext"
import useProductPublishedAlert from "~/hooks/useProductPublishedAlert"
import routes from "~/utilities/routes"
import { componentBackgroundColor } from "~/utilities/styles"
import trpc from "~/utilities/trpc"

import AiChapterGeneration from "./AiChapterGeneration"
import Chapter from "./Chapter"

const Sidebar: React.FC<{
	course: {
		id: number
		title: string
		isPublished: boolean
		chapters: { id: number; title: string; pages: { id: number }[] }[]
	}
	reloadCourse: () => Promise<unknown>
	selectedChapterId: number | null
	setSelectedChapterId: (chapterId: number) => void
	isAiModalOpen: boolean
	setAiModalOpen: (open: boolean) => void
	isAiPanelExpanded: boolean
	onAiPanelExpandedChange: (isExpanded: boolean) => void
	onCreateChapterClick: () => void
}> = ({
	course,
	reloadCourse,
	selectedChapterId,
	setSelectedChapterId,
	isAiModalOpen,
	setAiModalOpen,
	isAiPanelExpanded,
	onAiPanelExpandedChange,
	onCreateChapterClick,
}) => {
	const alert = useAlert()
	const platform = usePlatform()
	const publishedAlert = useProductPublishedAlert("course")
	const [chaptersOrder, setChaptersOrder] = useState<number[]>([])

	const canUseAi = platform?.enabledFeatures.includes("ai-generated-courses") ?? false

	useLayoutEffect(() => {
		setChaptersOrder(course.chapters.map(chapter => chapter.id))
	}, [course.chapters])

	const handlePublishCourse = async () => {
		const dialog = await alert.confirm(
			"Publish course",
			"Are you sure you want to publish this course? This action cannot be undone.",
			{ isDestructive: false },
		)
		if (!dialog.result) return

		await trpc.course.publish.mutate(course.id)
		await reloadCourse()

		dialog.close()

		await publishedAlert.show({ itemId: course.id })
	}

	const handleChapterDragEnd = async (event: DragEndEvent) => {
		const { active, over } = event
		if (over === null || over.id === active.id) return

		const newOrder = arrayMove(
			chaptersOrder,
			chaptersOrder.indexOf(Number(active.id)),
			chaptersOrder.indexOf(Number(over.id)),
		)
		setChaptersOrder(newOrder)

		for (const chapter of course.chapters) {
			trpc.course.updateChapter.mutate({ id: chapter.id, data: { sortOrder: newOrder.indexOf(chapter.id) } })
		}
	}

	return (
		<Container>
			<AiChapterGeneration
				isOpen={isAiModalOpen}
				courseId={course.id}
				onClose={() => setAiModalOpen(false)}
				onComplete={async () => {
					await reloadCourse()
					setAiModalOpen(false)
				}}
			/>
			<Header>
				<BackButton onClick={routes.course.index()}>
					<ArrowIcon direction="left" className="mr-2 size-6" />
					Courses
				</BackButton>
				<div className="flex items-center gap-2">
					<Tooltip tooltip="Exam">
						<Link to={routes.course.exam(course.id)}>
							<ExamIcon className="text-primary size-6" />
						</Link>
					</Tooltip>
					<Tooltip tooltip="Settings">
						<Link to={routes.course.settings(course.id)}>
							<SettingsIcon className="text-primary size-6" />
						</Link>
					</Tooltip>
				</div>
			</Header>
			<CourseTitle>{course.title}</CourseTitle>
			<Divider />
			{!course.isPublished && (
				<PublishCourseButton onClick={handlePublishCourse}>Publish course</PublishCourseButton>
			)}
			{canUseAi && (
				<Button
					variant="secondary"
					onClick={() => onAiPanelExpandedChange(!isAiPanelExpanded)}
					className={cn("mx-5 mb-4", isAiPanelExpanded && "bg-primary/10")}
				>
					<IconSparkles className="size-4" />
					{isAiPanelExpanded ? "Hide Forento AI" : "Edit with Forento AI"}
				</Button>
			)}
			<ChaptersHeader>
				<h2 className="text-lg font-semibold">Chapters</h2>
				<div className="flex items-center gap-2">
					{course.chapters.length > 0 && canUseAi && (
						<Tooltip tooltip="Generate chapter with Forento AI">
							<button
								type="button"
								onClick={() => setAiModalOpen(true)}
								className="bg-primary cursor-pointer rounded-full p-1"
							>
								<IconSparkles className="size-4 text-white" />
							</button>
						</Tooltip>
					)}
					<Tooltip tooltip="Create chapter">
						<button
							type="button"
							onClick={onCreateChapterClick}
							className="bg-primary cursor-pointer rounded-full p-1"
						>
							<AddIcon className="size-4 text-white" />
						</button>
					</Tooltip>
				</div>
			</ChaptersHeader>
			<div>
				<SortableListContext items={chaptersOrder} onDragEnd={handleChapterDragEnd}>
					{[...course.chapters]
						.sort((a, b) => chaptersOrder.indexOf(a.id) - chaptersOrder.indexOf(b.id))
						.map(chapter => (
							<Chapter
								key={chapter.id}
								isSelected={selectedChapterId === chapter.id}
								onClick={() => setSelectedChapterId(chapter.id)}
								chapter={chapter}
							/>
						))}
				</SortableListContext>
			</div>
		</Container>
	)
}

const Container = styled.div`
	flex: 0 0 350px;
	background-color: ${componentBackgroundColor};
	padding: 24px 0;
	display: flex;
	flex-direction: column;

	@media (max-width: 1024px) {
		flex-basis: 285px;
	}
`

const Header = styled.div`
	display: flex;
	justify-content: space-between;
	margin: 0 20px 16px;
`

const BackButton = styled(Button)`
	font-weight: 600;
	font-size: 14px;
	display: flex;
	align-items: center;
`

const CourseTitle = styled.p`
	font-size: 18px;
	font-weight: 600;
	margin: 0 20px 16px;
`

const Divider = styled.hr`
	border: none;
	border-bottom: 1px solid rgba(0, 0, 0, 0.1);
	margin: 0 20px 16px;
`

const PublishCourseButton = styled(Button).attrs({ variant: "primary" })`
	margin: 0 20px 16px;
`

const ChaptersHeader = styled.div`
	margin: 0 20px 32px;
	display: flex;
	justify-content: space-between;
	align-items: center;
`

export default Sidebar
